import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { CarouselProvider, DotGroup } from 'pure-react-carousel';

import BlockSection from '~/components/wrappers/BlockSection';
import IllustrationsBackground from '~/components/wrappers/IllustrationsBackground';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import Image from '~/components/base/Image';
import QuoteCarouselComponent from '~/components/QuoteCarousel';

import quoteGraphic from '~/images/singleIllustrations/quote.png';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes
} from '~/utilities/propTypes';

const MobileQuoteGraphicWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: none;
    }
`;

const MobileQuoteGraphic = styled(Image)`
    max-width: 110px;
`;

const StyledDotGroup = styled(DotGroup)`
    height: 32px;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 40px;

    .carousel__dot {
        height: 18px;
        width: 18px;
        background-color: ${({ theme }) => theme.colors.primary.yellow};
        border-radius: 50%;
        position: relative;

        &.carousel__dot--selected {
            &::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background-color: ${({ theme }) => theme.colors.primary.red};
                z-index: ${({ theme }) => theme.zIndex.backgroundColor};
            }

            &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 25px;
                height: 25px;
                border-radius: 50%;
                background-color: ${({ theme }) => theme.colors.primary.white};
                z-index: ${({ theme }) => theme.zIndex.backgroundColor};
            }
        }
    }
`;

const StyledIllustrationsBackground = styled(IllustrationsBackground)`
    display: none;
    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: block;
    }
`;

const QuoteCarousel = ({
    quotes,
    globalBlockOptions = DefaultGlobalBlockOptions,
    attributes = DefaultAcfAttributes
}) => {
    const colRef = useRef();

    return (
        <BlockSection {...{ globalBlockOptions, attributes }} padding="large">
            <StyledIllustrationsBackground backgroundGraphic="quoteCarousel" />
            <CarouselProvider
                naturalSlideWidth={1200}
                naturalSlideHeight={390}
                totalSlides={quotes.length}
                orientation="vertical"
            >
                <Row>
                    <Column span={12}>
                        <MobileQuoteGraphicWrapper>
                            <MobileQuoteGraphic
                                sourceUrl={quoteGraphic}
                                objectFit="contain"
                            />
                        </MobileQuoteGraphicWrapper>
                    </Column>
                    <Column
                        spanTablet={9}
                        startTablet={2}
                        span={12}
                        ref={colRef}
                    >
                        <QuoteCarouselComponent
                            {...{ quotes }}
                            wrapperRef={colRef}
                        />
                    </Column>
                    {quotes.length > 1 && (
                        <Column span={12}>
                            <StyledDotGroup />
                        </Column>
                    )}
                </Row>
            </CarouselProvider>
        </BlockSection>
    );
};

QuoteCarousel.propTypes = {
    quotes: PropTypes.arrayOf(
        PropTypes.shape({
            quote: PropTypes.string.isRequired,
            citation: PropTypes.string
        })
    ).isRequired,
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes
};

export default QuoteCarousel;
